export type AgentAuthErrorCode =
  | "AUTH_TOKEN_EXPIRED"
  | "AUTH_TOKEN_INVALID"
  | "AUTH_TOKEN_MISSING";

export interface AgentServerErrorPayload {
  status: number;
  code: string;
  error?: string;
  message?: string;
}

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function toAgentServerErrorPayload(
  value: Record<string, unknown>
): AgentServerErrorPayload | null {
  const status = value.status;
  const code = value.code;
  const error = value.error;
  const message = value.message;

  if (typeof status !== "number" || !Number.isFinite(status)) {
    return null;
  }

  if (typeof code !== "string" || code.trim().length === 0) {
    return null;
  }

  return {
    status,
    code,
    ...(typeof error === "string" ? { error } : {}),
    ...(typeof message === "string" ? { message } : {})
  };
}

function parseJsonPayload(text: string): AgentServerErrorPayload | null {
  if (!text.trim().startsWith("{")) {
    return null;
  }

  try {
    const parsed = JSON.parse(text) as unknown;
    if (!isObject(parsed)) {
      return null;
    }

    return toAgentServerErrorPayload(parsed);
  } catch {
    return null;
  }
}

export function parseAgentServerErrorPayload(
  error: unknown
): AgentServerErrorPayload | null {
  if (!error) {
    return null;
  }

  if (typeof error === "string") {
    return parseJsonPayload(error);
  }

  if (error instanceof Error) {
    const fromMessage = parseJsonPayload(error.message);
    if (fromMessage) {
      return fromMessage;
    }

    const errorWithCause = error as Error & { cause?: unknown };
    return parseAgentServerErrorPayload(errorWithCause.cause);
  }

  if (!isObject(error)) {
    return null;
  }

  const directPayload = toAgentServerErrorPayload(error);
  if (directPayload) {
    return directPayload;
  }

  const response = error.response;
  if (!isObject(response)) {
    return null;
  }

  const fromResponse = toAgentServerErrorPayload(response);
  if (fromResponse) {
    return fromResponse;
  }

  if (typeof response.body === "string") {
    return parseJsonPayload(response.body);
  }

  return null;
}

export function getAgentServerErrorMessage(error: unknown): string {
  const payload = parseAgentServerErrorPayload(error);
  if (payload?.error) {
    return payload.error;
  }

  if (payload?.message) {
    return payload.message;
  }

  if (error instanceof Error && error.message) {
    return error.message;
  }

  return "The request failed. Please try again.";
}

export function isTokenExpiredServerError(error: unknown): boolean {
  const payload = parseAgentServerErrorPayload(error);
  return payload?.status === 401 && payload.code === "AUTH_TOKEN_EXPIRED";
}
